/** The route tree, as data.
 *
 * Separate from `router.tsx` for one reason: `createBrowserRouter` reads
 * `window.history` the moment it is called, and the frontend suite runs with
 * `environment: 'node'`. Keeping the tree here lets the wiring be asserted
 * without a DOM — see route-tree.test.ts — while `router.tsx` stays the single
 * place the browser router is constructed.
 *
 * Portal routes are SPEC section 8; agent routes are SPEC section 9.
 *
 * ## Why both trees carry an `errorElement` and a catch-all
 *
 * Every screen in this app is a `lazy()` chunk, and `infra/nginx/default.conf`
 * answers a request for a hashed asset it does not have with 404 —
 * deliberately, because the alternative was index.html wearing a `.js` name.
 * The cost of that correctness lands here: a tab left open across a frontend
 * release asks for a chunk hash that no longer exists, the dynamic import
 * rejects, and the rejection goes straight past `<Suspense>`, which catches
 * *pending* and never *failed*.
 *
 * Until the pre-go-live audit there was no boundary at all, so that unmounted
 * the whole application and left React Router's own development error page. The
 * same hole answered every unmatched URL. The nginx comment said the client
 * "reloads and picks up the current index.html"; nothing in the app did that.
 * `components/RouteError.tsx` is the piece that makes the sentence true.
 *
 * `errorElement` sits on the layout route rather than around it, so the shell —
 * navigation, the brand, the sign-out control — survives a failed page instead
 * of dropping the user onto a bare screen with nothing to click.
 */
import type { RouteObject } from 'react-router-dom'

import { AgentLayout } from './agent/AgentLayout'
import { agentRoutes } from './agent/routes'
import { AnniversaryLayout, AnniversaryPage, OverviewPage } from './anniversary/AnniversaryLayout'
import { NotFound, RouteError } from './components/RouteError'
import { PortalLayout } from './portal/PortalLayout'
import { portalRoutes } from './portal/routes'

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <PortalLayout />,
    errorElement: <RouteError />,
    children: [...portalRoutes, { path: '*', element: <NotFound /> }],
  },
  {
    path: '/agent',
    element: <AgentLayout />,
    errorElement: <RouteError />,
    children: [...agentRoutes, { path: '*', element: <NotFound /> }],
  },
  // The 25th Anniversary launch experience. Public, full-bleed, and its own
  // tree so it inherits neither the portal's header nor its reading width —
  // see anniversary/Anniversary.tsx. One route, one navigation entry.
  {
    path: '/anniversary',
    element: <AnniversaryLayout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <AnniversaryPage /> },
      { path: '*', element: <NotFound /> },
    ],
  },
  // Where "Launch Helpdesk" lands: the thank-you, what the desk does, and the
  // way in. Public, because it has to render before anyone has signed in.
  {
    path: '/overview',
    element: <AnniversaryLayout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]
